/**
 * カスタム項目（台帳・割り当て・値）を画面で扱う純関数。
 *
 * 有効な項目の解決規則は DB の public.effective_category_fields() と同じ:
 *   - 最上位から自分までの祖先の割り当てを合わせる（継承）
 *   - 同じ key が複数段にあれば近い段（深い方）の required / position が勝つ
 *   - 並びは「上位の割り当て → 下位の割り当て」、同じ段では position、次に台帳の sort_order
 * **規則を変えるときは DB 側も変える。**
 */
import type { Asset, AssetField, AssetFieldType, Category, CategoryField, FieldOption } from './supabase.ts'
import { categoryPath, descendantIds } from './categories.ts'

export type EffectiveField = AssetField & {
  required: boolean
  position: number
  /** 割り当て元のカテゴリ id と深さ（最上位 = 1）。継承の表示に使う。 */
  assignedTo: string
  assignedDepth: number
}

export const FIELD_TYPE_LABEL: Record<AssetFieldType, string> = {
  text: '文字',
  number: '数値',
  date: '日付',
  boolean: 'はい／いいえ',
  enum: '選択（1つ）',
  multi_enum: '選択（複数）',
}

export const FIELD_KEY_RE = /^[a-z][a-z0-9_]{1,62}$/

export const isValidFieldKey = (key: string): boolean => FIELD_KEY_RE.test(key)

/** カテゴリに有効な項目（継承込み）。category が null（未分類）なら空。 */
export function effectiveFields(
  categoryId: string | null,
  categories: Category[],
  fields: AssetField[],
  assignments: CategoryField[],
): EffectiveField[] {
  if (!categoryId) return []
  const chain = categoryPath(categories, categoryId) // 最上位 → 自分
  const byKey = new Map(fields.map((f) => [f.key, f]))
  const chosen = new Map<string, EffectiveField>()
  chain.forEach((cat, i) => {
    for (const a of assignments) {
      if (a.category_id !== cat.id) continue
      const field = byKey.get(a.field_key)
      if (!field) continue
      // 深い段ほど後に回るので、上書きで「近い方が勝つ」になる
      chosen.set(a.field_key, {
        ...field,
        required: a.required,
        position: a.position,
        assignedTo: cat.id,
        assignedDepth: i + 1,
      })
    }
  })
  return [...chosen.values()].sort(
    (x, y) =>
      x.assignedDepth - y.assignedDepth ||
      x.position - y.position ||
      x.sort_order - y.sort_order ||
      x.label.localeCompare(y.label, 'ja'),
  )
}

/**
 * 部分木（自分＋配下）のどこかで有効な項目の和集合。絞り込み（ファセット）に使う。
 * 'all' なら全カテゴリ。並びは台帳の sort_order → ラベル。
 */
export function fieldsForSubtree(
  categoryId: string | 'all',
  categories: Category[],
  fields: AssetField[],
  assignments: CategoryField[],
): AssetField[] {
  const targets = categoryId === 'all' ? categories.map((c) => c.id) : [...descendantIds(categories, categoryId)]
  const keys = new Set<string>()
  for (const id of targets) {
    for (const f of effectiveFields(id, categories, fields, assignments)) keys.add(f.key)
  }
  return fields
    .filter((f) => keys.has(f.key))
    .sort((a, b) => a.sort_order - b.sort_order || a.label.localeCompare(b.label, 'ja'))
}

const optionLabel = (options: FieldOption[], value: string): string =>
  options.find((o) => o.value === value)?.label ?? value

/** 値の表示文字列。未設定なら null（呼び出し側で「—」等にする）。 */
export function formatFieldValue(field: Pick<AssetField, 'type' | 'options'>, value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null
  switch (field.type) {
    case 'boolean':
      return value === true ? 'はい' : value === false ? 'いいえ' : null
    case 'enum':
      return typeof value === 'string' ? optionLabel(field.options, value) : null
    case 'multi_enum':
      return Array.isArray(value) && value.length
        ? value.filter((v): v is string => typeof v === 'string').map((v) => optionLabel(field.options, v)).join('、')
        : null
    case 'number':
      // 桁区切りは付けない。年式・型番のような「数字の並び」が多く、2,019 は誤り
      return String(value)
    case 'text':
    case 'date':
      return String(value)
  }
}

export type FacetValue = { value: string; label: string; count: number }

/**
 * 絞り込み用の値の一覧。値ごとの件数を付ける。
 * multi_enum は要素ごと、boolean は 'true' / 'false' をキーにする。
 * enum 系は選択肢の定義順、それ以外は件数の多い順 → 値の順。
 */
export function facetValues(field: AssetField, assets: Pick<Asset, 'attributes'>[]): FacetValue[] {
  const counts = new Map<string, number>()
  for (const a of assets) {
    const v = a.attributes?.[field.key]
    for (const key of facetKeysOf(field, v)) counts.set(key, (counts.get(key) ?? 0) + 1)
  }
  const entries = [...counts.entries()].map(([value, count]) => ({
    value,
    label: facetLabel(field, value),
    count,
  }))
  if (field.type === 'enum' || field.type === 'multi_enum') {
    const order = new Map(field.options.map((o, i) => [o.value, i]))
    return entries.sort((a, b) => (order.get(a.value) ?? 1e9) - (order.get(b.value) ?? 1e9))
  }
  if (field.type === 'boolean') {
    return entries.sort((a, b) => (a.value === 'true' ? -1 : 1) - (b.value === 'true' ? -1 : 1))
  }
  return entries.sort((a, b) => b.count - a.count || a.value.localeCompare(b.value, 'ja'))
}

/** 素材の値から、絞り込みのキー（文字列）を取り出す。未設定なら空。 */
export function facetKeysOf(field: Pick<AssetField, 'type'>, value: unknown): string[] {
  if (value === null || value === undefined || value === '') return []
  if (field.type === 'multi_enum') {
    return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : []
  }
  if (field.type === 'boolean') return typeof value === 'boolean' ? [String(value)] : []
  return [String(value)]
}

export function facetLabel(field: Pick<AssetField, 'type' | 'options'>, value: string): string {
  if (field.type === 'enum' || field.type === 'multi_enum') return optionLabel(field.options, value)
  if (field.type === 'boolean') return value === 'true' ? 'はい' : 'いいえ'
  return value
}

/** 選んだ値（OR）に素材が合うか。何も選んでいなければ常に真。 */
export function matchesFacet(field: Pick<AssetField, 'type'>, value: unknown, selected: ReadonlySet<string>): boolean {
  if (selected.size === 0) return true
  return facetKeysOf(field, value).some((k) => selected.has(k))
}

/** 新しい選択肢のコード。既存と重ならない opt_N。 */
export function nextOptionValue(options: FieldOption[]): string {
  const used = new Set(options.map((o) => o.value))
  let n = options.length + 1
  while (used.has(`opt_${n}`)) n += 1
  return `opt_${n}`
}
